import { useState } from "react";
import { Link } from "react-router-dom";
import { http, formatError } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { ArrowLeft, EnvelopeSimple } from "@phosphor-icons/react";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await http.post("/auth/password/request-reset", { email });
      setSent(true);
      toast.success(data?.message || "Reset link sent.");
    } catch (err) { toast.error(formatError(err)); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#f8fafc]">
      <div className="hidden lg:block relative">
        <img src="https://images.unsplash.com/photo-1706808849780-7a04fbac83ef?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTJ8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBhZnJpY2FuJTIwcmVhbCUyMGVzdGF0ZSUyMGFyY2hpdGVjdHVyZXxlbnwwfHx8fDE3ODU1Mzk4NDJ8MA&ixlib=rb-4.1.0&q=85" alt="" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-[#0a1128]/70" />
        <div className="absolute inset-0 p-12 flex flex-col justify-between text-white">
          <Link to="/" className="flex items-center gap-2">
            <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-white/10"><span className="text-[#00b4d8] font-black text-2xl font-display">M</span></span>
            <span className="font-display font-black text-2xl">MAYOTO</span>
          </Link>
          <div>
            <p className="text-[#d4af37] font-display font-bold tracking-[0.25em] text-xs">FORGOT PASSWORD</p>
            <h1 className="mt-3 font-display font-black text-5xl leading-tight max-w-md">Locked out? We'll get you back in.</h1>
            <p className="mt-4 text-slate-300 max-w-md">Enter your email and we'll send you a secure link to choose a new password.</p>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 lg:p-12">
        <div className="w-full max-w-md">
          <Link to="/login" className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-[#00b4d8] mb-6" data-testid="forgot-back-login"><ArrowLeft size={16} /> Back to login</Link>
          <h2 className="font-display font-black text-3xl text-[#0a1128]" data-testid="forgot-title">Reset your password</h2>
          <p className="mt-2 text-slate-600">We'll email a secure link to reset your password. The link is valid for 30 minutes.</p>

          {sent ? (
            <div className="mt-8 rounded-2xl bg-emerald-50 border border-emerald-200 p-6" data-testid="forgot-sent">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-emerald-500 grid place-items-center"><EnvelopeSimple size={20} weight="bold" className="text-white" /></div>
                <p className="font-display font-bold text-emerald-900">Check your inbox</p>
              </div>
              <p className="mt-3 text-sm text-emerald-900">If an account with <span className="font-semibold">{email}</span> exists, a reset link is on its way. It expires in 30 minutes.</p>
              <p className="mt-3 text-xs text-emerald-800">Didn't get anything? Check your spam folder — or <button className="underline" onClick={() => setSent(false)}>try a different email</button>.</p>
            </div>
          ) : (
            <form onSubmit={submit} className="mt-8 space-y-5" data-testid="forgot-form">
              <div>
                <Label htmlFor="email">Email</Label>
                <Input id="email" type="email" required value={email} onChange={e => setEmail(e.target.value)} data-testid="forgot-email" />
              </div>
              <Button type="submit" disabled={loading || !email} className="btn-shine w-full bg-[#00b4d8] hover:bg-[#0096c7] rounded-full py-6 text-base" data-testid="forgot-submit">
                {loading ? "Sending reset link…" : "Send reset link"}
              </Button>
            </form>
          )}

          <p className="mt-6 text-sm text-slate-600 text-center">
            Remembered it? <Link to="/login" className="text-[#00b4d8] font-semibold hover:underline">Sign in</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
